<template>
    <div class="h-dvh bg-surface-sunken flex flex-col overflow-hidden">
        <CompetencyAssessmentCompleted
            v-if="phase === 'completed'"
            :download_report_url="activeDownloadReportUrl"
            :coaching_session_url="activeCoachingSessionUrl"
            :completed-strengths="completedStrengths"
            :completed-opportunities="completedOpportunities"
            :grouped-questions="groupedQuestions"
            :completed_responses="activeCompletedResponses"
            :rating_labels="rating_labels"
            :confirming-retake="confirmingRetake"
            :is-retaking="isRetaking"
            :retake-error="retakeError"
            :is-newly-submitted="isNewlySubmitted"
            @start-retake="confirmingRetake = true"
            @cancel-retake="confirmingRetake = false"
            @confirm-retake="handleRetake" />

        <CompetencyAssessmentIntro
            v-else-if="phase !== 'assessment'"
            :phase="introPhase"
            :is-starting-assessment="isStartingAssessment"
            :start-error="startError"
            :rating_labels="rating_labels"
            :rating_descriptions="rating_descriptions"
            @intro-prev="introPrev"
            @intro-next="introNext" />

        <!-- ── ASSESSMENT PHASE ── -->
        <template v-else-if="phase === 'assessment'">
            <!-- Header -->
            <div class="shrink-0 bg-surface-raised border-b border-border px-6 py-4">
                <div class="max-w-3xl mx-auto flex items-center justify-between">
                    <h1 class="title-body-bold text-ink">{{ t("assessments.competency.pageTitle") }}</h1>
                    <!-- Competency step dots -->
                    <div class="flex items-center gap-2">
                        <button
                            v-for="(group, i) in groupedQuestions"
                            :key="i"
                            :aria-label="t('assessments.competency.questions.goToCompetency', { competency: group.competency })"
                            :disabled="isSubmitting"
                            :class="[
                                'transition-[width,background-color] duration-(--motion-duration-normal) motion-reduce:transition-[background-color] rounded-full',
                                i === currentStep ? 'w-6 h-2.5 bg-control-accent' : hasRatedAny(i) ? 'w-2.5 h-2.5 bg-control-accent/40' : 'w-2.5 h-2.5 bg-border',
                            ]"
                            @click="currentStep = i" />
                    </div>
                </div>
            </div>

            <!-- Step content -->
            <div ref="scrollContainer" class="flex-1 overflow-y-auto">
                <Transition :name="transitionName" mode="out-in">
                    <div :key="currentStep" class="max-w-3xl mx-auto w-full px-6 py-10">
                        <!-- Competency title -->
                        <div class="mb-6">
                            <!-- One-off: 12px font-bold tracking-widest eyebrow; no exact utility match -->
                            <div class="flex flex-wrap items-center gap-2 details font-bold tracking-widest text-ink uppercase mb-2">
                                <span>{{ t("assessments.competency.questions.stepProgress", { current: currentStep + 1, total: groupedQuestions.length }) }}</span>
                                <div class="h-px flex-1 bg-border"></div>
                                <RatingGuideDialog
                                    :rating-labels="rating_labels"
                                    :rating-short-descriptions="rating_short_descriptions"
                                    :rating-descriptions="rating_descriptions"
                                    show-not-applicable />
                            </div>
                            <h2 class="title-body-bold text-ink">
                                {{ groupedQuestions[currentStep]?.competency }}
                            </h2>
                            <p v-if="groupedQuestions[currentStep]?.definition" class="body-small text-ink-muted mt-1">
                                {{ groupedQuestions[currentStep]?.definition }}
                            </p>
                            <p class="body-small mt-1">
                                <span
                                    :class="
                                        groupRatedCount(currentStep) === groupedQuestions[currentStep]?.questions.length
                                            ? 'text-notification-success-text font-semibold'
                                            : 'text-notification-warning-text font-semibold'
                                    ">
                                    {{
                                        t("assessments.competency.questions.groupAnswered", {
                                            answered: groupRatedCount(currentStep),
                                            total: groupedQuestions[currentStep]?.questions.length ?? 0,
                                        })
                                    }}
                                </span>
                            </p>
                        </div>

                        <!-- Questions with sliders -->
                        <div class="space-y-5">
                            <div
                                v-for="q in groupedQuestions[currentStep]?.questions"
                                :key="q.index"
                                :data-question-index="q.index"
                                :class="[
                                    'bg-surface-raised rounded-2xl border shadow-sm p-5 px-10 transition-colors duration-300',
                                    highlightedUnselected.has(q.index) ? 'border-notification-warning-border' : 'border-border',
                                ]">
                                <div class="flex items-start justify-between gap-4 mb-4">
                                    <div class="flex-1">
                                        <h3 :id="questionTitleId(q.index)" class="body-bold text-ink">{{ q.title }}</h3>
                                        <p class="body-small text-ink-muted mt-1">{{ q.description }}</p>
                                    </div>
                                    <!-- Rating badge -->
                                    <div
                                        :class="[
                                            'shrink-0 flex flex-col items-center justify-center w-16 h-16 rounded-xl transition-[background-color,color,border-color,border-width] duration-(--motion-duration-normal) motion-reduce:transition-[background-color,color,border-color] overflow-hidden',
                                            !selectedQuestions.has(q.index)
                                                ? 'bg-surface-sunken border-2 border-dashed border-border text-ink-muted'
                                                : ratings[q.index] === 0
                                                  ? 'bg-surface-sunken text-ink-muted'
                                                  : 'bg-control-accent text-ink-inverse',
                                        ]">
                                        <span class="title-body-bold leading-none">
                                            {{
                                                !selectedQuestions.has(q.index)
                                                    ? UNANSWERED_MARK
                                                    : ratings[q.index] === 0
                                                      ? t("assessments.competency.scale.notApplicable")
                                                      : ratings[q.index]
                                            }}
                                        </span>
                                        <span class="text-[10px] font-semibold leading-tight mt-1 text-center w-full px-1 truncate">
                                            {{ (ratings[q.index] ?? 0) > 0 ? (rating_labels[String(ratings[q.index] ?? 0)] ?? "") : "" }}
                                        </span>
                                    </div>
                                </div>

                                <!-- Slider: 0 = N/A, 1–5 = rating -->
                                <div role="group" :aria-labelledby="questionTitleId(q.index)" class="mt-3">
                                    <input
                                        type="range"
                                        min="0"
                                        max="5"
                                        step="1"
                                        :value="ratings[q.index]"
                                        :aria-label="t('assessments.competency.questions.ratingFor', { question: q.title })"
                                        :aria-valuetext="selectedQuestions.has(q.index) ? ratingValueText(ratings[q.index] ?? 0) : t('assessments.competency.questions.notRated')"
                                        :disabled="isSubmitting"
                                        :class="['slider w-full', !selectedQuestions.has(q.index) ? 'slider-unselected' : '']"
                                        :style="sliderTrackStyle(q.index)"
                                        @click="markCurrentSliderValueAsSelected(q.index)"
                                        @input="(e) => setRating(q.index, Number((e.target as HTMLInputElement).value))"
                                        @change="(e) => saveResponse(q.index, Number((e.target as HTMLInputElement).value))" />
                                    <!-- Labels: N/A at left, grades 1–5 centered at their exact slider positions -->
                                    <div class="relative mt-1 pb-10 overflow-visible">
                                        <!-- N/A at 0% — left-aligned -->
                                        <button
                                            class="absolute top-0 left-2 -translate-x-1/2 w-[15%] flex flex-col items-center text-center transition-colors duration-150 cursor-pointer"
                                            :class="selectedQuestions.has(q.index) && ratings[q.index] === 0 ? 'text-ink' : 'text-ink-muted'"
                                            :disabled="isSubmitting"
                                            type="button"
                                            @click="
                                                setRating(q.index, 0);
                                                saveResponse(q.index, 0);
                                            ">
                                            <span class="details leading-tight">{{ t("assessments.competency.scale.notApplicable") }}</span>
                                            <span class="text-[11px] leading-tight mt-1">{{ t("assessments.competency.scale.notApplicableLabel") }}</span>
                                        </button>
                                        <!-- Grades 1–5 centered at 20%, 40%, 60%, 80%, 100% -->
                                        <button
                                            v-for="i in 5"
                                            :key="i"
                                            class="absolute top-0 -translate-x-1/2 w-[15%] flex flex-col items-center text-center transition-colors duration-150 cursor-pointer"
                                            :style="{ left: `${i * 20}%` }"
                                            :class="selectedQuestions.has(q.index) && ratings[q.index] === i ? 'text-ink' : 'text-ink-muted'"
                                            :disabled="isSubmitting"
                                            type="button"
                                            @click="
                                                setRating(q.index, i);
                                                saveResponse(q.index, i);
                                            ">
                                            <span class="details leading-tight">{{ ratingValueText(i) }}</span>
                                            <span class="text-[11px] leading-tight mt-1">{{ rating_short_descriptions[String(i)] }}</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Transition>
            </div>

            <!-- Footer nav -->
            <div class="shrink-0 bg-surface-raised border-t border-border px-6 py-4">
                <div class="max-w-3xl mx-auto flex items-center gap-3">
                    <BaseButton v-if="currentStep > 0" :disabled="isSubmitting" theme="tertiary" @click="goBack"> {{ t("assessments.competency.nav.back") }} </BaseButton>
                    <div class="flex-1 body text-ink-muted text-center">
                        {{ t("assessments.competency.questions.totalRated", { rated: ratedCount, total: questions.length }) }}
                        <p v-if="selectionError" role="alert" class="details text-notification-warning-text mt-1">{{ selectionError }}</p>
                        <p v-else-if="submitError" role="alert" class="details text-notification-warning-text mt-1">{{ submitError }}</p>
                    </div>
                    <BaseButton v-if="currentStep < groupedQuestions.length - 1" :disabled="isSubmitting" theme="primary" @click="goNext">
                        {{ t("assessments.competency.nav.next") }}
                    </BaseButton>
                    <BaseButton v-else theme="primary" class="whitespace-normal sm:whitespace-nowrap" :disabled="isSubmitting" @click="handleSubmit">
                        <span v-if="isSubmitting">{{ t("assessments.competency.nav.submitting") }}</span>
                        <span v-else>{{ t("assessments.competency.nav.submit") }}</span>
                    </BaseButton>
                </div>
            </div>
        </template>
        <!-- ── END ASSESSMENT PHASE ── -->
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import RatingGuideDialog from "~vue/Assessments/RatingGuideDialog.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import CompetencyAssessmentCompleted from "~vue/pages/competencyAssessment/CompetencyAssessmentCompleted.vue";
import CompetencyAssessmentIntro from "~vue/pages/competencyAssessment/CompetencyAssessmentIntro.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

interface Question {
    index: number;
    competency: string;
    title: string;
    description: string;
}

interface Props {
    questions: Question[];
    competency_definitions: Record<string, string>;
    rating_labels: Record<string, string>;
    rating_short_descriptions: Record<string, string>;
    rating_descriptions: Record<string, string>;
    saved_responses?: Record<number, number>;
    draft_assessment_id?: string | null;
    completed_responses?: Record<number, number>;
    coaching_session_url?: string | null;
    completed_assessment_id?: string | null;
    download_report_url?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
    saved_responses: () => ({}),
    draft_assessment_id: null,
    completed_responses: () => ({}),
    coaching_session_url: null,
    completed_assessment_id: null,
    download_report_url: null,
});

const { t } = useI18n();

// Punctuation only, so there is nothing to translate: the badge shows an em dash until the question is answered.
const UNANSWERED_MARK = "—";

const isCompleted = computed(() => !!props.completed_assessment_id);
const hasDraft = computed(() => !!props.draft_assessment_id);

type Phase = "disclaimer" | "instructions" | "example" | "assessment" | "completed";
const INTRO_PHASES: Phase[] = ["disclaimer", "instructions", "example"];
const phase = ref<Phase>(isCompleted.value ? "completed" : hasDraft.value ? "assessment" : "disclaimer");

const assessmentId = ref<string | null>(props.draft_assessment_id ?? null);
const confirmingRetake = ref(false);
const isRetaking = ref(false);
const retakeError = ref<string | null>(null);
const isStartingAssessment = ref(false);
const startError = ref<string | null>(null);
const isNewlySubmitted = ref(false);
const freshCoachingSessionUrl = ref<string | null>(null);
const freshDownloadReportUrl = ref<string | null>(null);
const freshCompletedResponses = ref<Record<number, number> | null>(null);

const activeCoachingSessionUrl = computed(() => freshCoachingSessionUrl.value ?? props.coaching_session_url ?? null);
const activeDownloadReportUrl = computed(() => freshDownloadReportUrl.value ?? props.download_report_url ?? null);
const activeCompletedResponses = computed(() => freshCompletedResponses.value ?? props.completed_responses ?? {});

async function handleRetake() {
    retakeError.value = null;
    isRetaking.value = true;
    try {
        const { error } = await useFetch("/competency-assessment/actions/")
            .post(JSON.stringify({ intent: "retake" }), "application/json")
            .json();

        if (error.value) {
            retakeError.value = t("assessments.competency.questions.errors.retakeFailed");
            return;
        }

        ratings.value = Object.fromEntries(props.questions.map((q) => [q.index, 0]));
        selectedQuestions.value = new Set();
        highlightedUnselected.value = new Set();
        selectionError.value = null;
        pendingSaveRequests.clear();
        failedSaveByQuestion.value = {};
        saveVersionByQuestion.value = {};
        submitError.value = null;
        currentStep.value = 0;
        assessmentId.value = null;
        freshCoachingSessionUrl.value = null;
        freshDownloadReportUrl.value = null;
        freshCompletedResponses.value = null;
        isNewlySubmitted.value = false;
        confirmingRetake.value = false;
        phase.value = "disclaimer";
        void logUserInteraction(AmplitudeEventNames.COMPETENCY_ASSESSMENT_RETAKE);
    } catch {
        retakeError.value = t("assessments.competency.questions.errors.retakeFailed");
    } finally {
        isRetaking.value = false;
    }
}

async function introNext() {
    const idx = INTRO_PHASES.indexOf(phase.value);
    if (idx < INTRO_PHASES.length - 1) {
        startError.value = null;
        const nextPhase = INTRO_PHASES[idx + 1];
        if (!nextPhase) {
            return;
        }
        phase.value = nextPhase;
    } else {
        // Start a fresh assessment before entering assessment phase
        startError.value = null;
        isStartingAssessment.value = true;
        const { data, error } = await useFetch("/competency-assessment/actions/")
            .post(JSON.stringify({ intent: "start" }), "application/json")
            .json<{ assessment_id: string }>();
        isStartingAssessment.value = false;
        if (error.value || !data.value?.assessment_id) {
            startError.value = t("assessments.competency.questions.errors.startFailed");
            return;
        }
        assessmentId.value = data.value.assessment_id;
        void logUserInteraction(AmplitudeEventNames.COMPETENCY_ASSESSMENT_STARTED);
        phase.value = "assessment";
    }
}

function introPrev() {
    const idx = INTRO_PHASES.indexOf(phase.value);
    if (idx > 0) {
        const prevPhase = INTRO_PHASES[idx - 1];
        if (!prevPhase) {
            return;
        }
        phase.value = prevPhase;
    }
}

// Init from saved draft, fallback to 0 (N/A) for any unsaved question
const ratings = ref<Record<number, number>>(Object.fromEntries(props.questions.map((q) => [q.index, props.saved_responses?.[q.index] ?? 0])));
// Track which questions the user has explicitly interacted with (pre-populate from saved responses)
const selectedQuestions = ref<Set<number>>(new Set(Object.keys(props.saved_responses ?? {}).map(Number)));
// Highlighted unselected cards (amber border), shown after failed Next attempt
const highlightedUnselected = ref<Set<number>>(new Set());
const scrollContainer = ref<HTMLElement | null>(null);
const currentStep = ref(0);
const isSubmitting = ref(false);
const submitError = ref<string | null>(null);
const selectionError = ref<string | null>(null);
const transitionName = ref("slide-left");
const pendingSaveRequests = new Map<number, Promise<boolean>>();
const failedSaveByQuestion = ref<Record<number, boolean>>({});
const saveVersionByQuestion = ref<Record<number, number>>({});

onMounted(() => {
    void logUserInteraction(AmplitudeEventNames.COMPETENCY_ASSESSMENT_VIEWED, {
        is_completed: isCompleted.value,
        has_draft: hasDraft.value,
    });
});

const groupedQuestions = computed(() => {
    const groups: { competency: string; definition: string; questions: Question[] }[] = [];
    for (const q of props.questions) {
        const last = groups[groups.length - 1];
        if (last && last.competency === q.competency) {
            last.questions.push(q);
        } else {
            groups.push({ competency: q.competency, definition: props.competency_definitions[q.competency] ?? "", questions: [q] });
        }
    }
    return groups;
});

const introPhase = computed(() => (phase.value === "assessment" || phase.value === "completed" ? "disclaimer" : phase.value));

// Count only 1–5 ratings (not N/A) for the footer progress indicator
const ratedCount = computed(() => Object.values(ratings.value).filter((v) => v > 0).length);

// Completed screen: top 5 strengths (4–5, highest first) and top 5 opportunities (1–2, lowest first)
const completedStrengths = computed(() =>
    props.questions
        .filter((q) => (activeCompletedResponses.value[q.index] ?? 0) >= 4)
        .sort((a, b) => {
            const diff = (activeCompletedResponses.value[b.index] ?? 0) - (activeCompletedResponses.value[a.index] ?? 0);
            return diff !== 0 ? diff : a.index - b.index;
        })
        .slice(0, 5),
);
const completedOpportunities = computed(() =>
    props.questions
        .filter((q) => {
            const r = activeCompletedResponses.value[q.index] ?? 0;
            return r >= 1 && r <= 2;
        })
        .sort((a, b) => {
            const diff = (activeCompletedResponses.value[a.index] ?? 0) - (activeCompletedResponses.value[b.index] ?? 0);
            return diff !== 0 ? diff : a.index - b.index;
        })
        .slice(0, 5),
);

function questionTitleId(index: number) {
    return `competency-question-${index}-title`;
}

// The slider and the 1–5 buttons share this wording so a scale point's accessible name matches its visible label.
function ratingValueText(rating: number) {
    if (rating === 0) {
        return t("assessments.competency.scale.notApplicableLabel");
    }
    return t("assessments.competency.scale.point", { value: rating, label: props.rating_labels[String(rating)] ?? "" });
}

// Step dots show as "has progress" when any question in the group has a 1–5 rating
function hasRatedAny(stepIndex: number) {
    return groupedQuestions.value[stepIndex]?.questions.some((q) => (ratings.value[q.index] ?? 0) > 0) ?? false;
}

// Subtitle under competency title: count of explicitly selected questions in current group (including N/A)
function groupRatedCount(stepIndex: number) {
    return groupedQuestions.value[stepIndex]?.questions.filter((q) => selectedQuestions.value.has(q.index)).length ?? 0;
}

function setRating(index: number, value: number) {
    ratings.value = { ...ratings.value, [index]: value };
    selectedQuestions.value = new Set([...selectedQuestions.value, index]);
    highlightedUnselected.value.delete(index);
    highlightedUnselected.value = new Set(highlightedUnselected.value);
    if (highlightedUnselected.value.size === 0) {
        selectionError.value = null;
    }
}

function markCurrentSliderValueAsSelected(index: number) {
    if (selectedQuestions.value.has(index)) {
        return;
    }

    const value = ratings.value[index] ?? 0;
    setRating(index, value);
    saveResponse(index, value);
}

function delay(ms: number) {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

async function saveResponseWithRetry(index: number, value: number): Promise<boolean> {
    const maxAttempts = 3;
    const retryDelaysMs = [300, 800];
    const saveVersion = saveVersionByQuestion.value[index] ?? 0;

    failedSaveByQuestion.value = { ...failedSaveByQuestion.value, [index]: false };

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
            const { error } = await useFetch("/competency-assessment/actions/")
                .post(JSON.stringify({ intent: "save_response", assessment_id: assessmentId.value, question_index: index, rating: value }), "application/json")
                .json();

            if (!error.value) {
                if ((saveVersionByQuestion.value[index] ?? 0) === saveVersion) {
                    failedSaveByQuestion.value = { ...failedSaveByQuestion.value, [index]: false };
                }
                return true;
            }
        } catch {
            // Treat network/runtime exceptions like failed autosaves and continue retry logic.
        }

        if (attempt < maxAttempts) {
            const retryDelay = retryDelaysMs[attempt - 1] ?? 0;
            await delay(retryDelay);
        }
    }

    if ((saveVersionByQuestion.value[index] ?? 0) === saveVersion) {
        failedSaveByQuestion.value = { ...failedSaveByQuestion.value, [index]: true };
    }
    return false;
}

function saveResponse(index: number, value: number) {
    if (!assessmentId.value) {
        return;
    }
    const nextVersion = (saveVersionByQuestion.value[index] ?? 0) + 1;
    saveVersionByQuestion.value = { ...saveVersionByQuestion.value, [index]: nextVersion };

    const request = saveResponseWithRetry(index, value);
    pendingSaveRequests.set(index, request);
    void request.finally(() => {
        if (pendingSaveRequests.get(index) === request) {
            pendingSaveRequests.delete(index);
        }
    });
}

async function flushPendingSaves() {
    if (!pendingSaveRequests.size) {
        return;
    }
    await Promise.allSettled([...pendingSaveRequests.values()]);
}

function sliderTrackStyle(index: number) {
    const val = ratings.value[index] ?? 0;
    const pct = (val / 5) * 100;
    return {
        background: `linear-gradient(to right, var(--color-control-accent) ${pct}%, var(--color-control-border) ${pct}%)`,
    };
}

function goNext() {
    const currentQuestions = groupedQuestions.value[currentStep.value]?.questions ?? [];
    const unselected = currentQuestions.filter((q) => !selectedQuestions.value.has(q.index));
    if (unselected.length > 0) {
        selectionError.value = t("assessments.competency.questions.errors.selectionRequired");
        highlightedUnselected.value = new Set(unselected.map((q) => q.index));
        const firstIndex = unselected[0]?.index;
        if (firstIndex !== undefined) {
            scrollContainer.value?.querySelector(`[data-question-index="${firstIndex}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        return;
    }
    selectionError.value = null;
    highlightedUnselected.value = new Set();
    transitionName.value = "slide-left";
    currentStep.value += 1;
    scrollContainer.value?.scrollTo({ top: 0, behavior: "smooth" });
}

function goBack() {
    selectionError.value = null;
    transitionName.value = "slide-right";
    currentStep.value -= 1;
    scrollContainer.value?.scrollTo({ top: 0, behavior: "smooth" });
}

async function handleSubmit() {
    const currentQuestions = groupedQuestions.value[currentStep.value]?.questions ?? [];
    const unselected = currentQuestions.filter((q) => !selectedQuestions.value.has(q.index));
    if (unselected.length > 0) {
        selectionError.value = t("assessments.competency.questions.errors.selectionRequired");
        highlightedUnselected.value = new Set(unselected.map((q) => q.index));
        const firstIndex = unselected[0]?.index;
        if (firstIndex !== undefined) {
            scrollContainer.value?.querySelector(`[data-question-index="${firstIndex}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        return;
    }
    selectionError.value = null;
    highlightedUnselected.value = new Set();

    isSubmitting.value = true;
    submitError.value = null;

    try {
        await flushPendingSaves();

        if (Object.values(failedSaveByQuestion.value).some(Boolean)) {
            submitError.value = t("assessments.competency.questions.errors.saveFailed");
            return;
        }

        const { data, error } = await useFetch("/competency-assessment/actions/")
            .post(JSON.stringify({ intent: "submit", assessment_id: assessmentId.value }), "application/json")
            .json<{ coaching_session_url: string | null; download_report_url: string }>();

        if (!error.value && data.value?.download_report_url) {
            void logUserInteraction(AmplitudeEventNames.COMPETENCY_ASSESSMENT_SUBMITTED, {
                rated_count: ratedCount.value,
                total_questions: props.questions.length,
            });
            freshCoachingSessionUrl.value = data.value.coaching_session_url ?? null;
            freshDownloadReportUrl.value = data.value.download_report_url;
            freshCompletedResponses.value = { ...ratings.value };
            isNewlySubmitted.value = true;
            phase.value = "completed";
            return;
        }

        submitError.value = t("assessments.competency.questions.errors.submitFailed");
    } catch {
        submitError.value = t("assessments.competency.questions.errors.submitFailed");
    } finally {
        isSubmitting.value = false;
    }
}
</script>

<style scoped>
.slider {
    -webkit-appearance: none;
    appearance: none;
    height: 4px;
    border-radius: 9999px;
    outline: none;
    cursor: pointer;
}

.slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--color-control-accent);
    cursor: pointer;
    border: 3px solid var(--color-control-bg);
    box-shadow: var(--shadow-xs);
    transition: transform 0.1s ease;
}

.slider-unselected::-webkit-slider-thumb {
    background: var(--color-control-border-strong);
    box-shadow: none;
}

.slider-unselected::-moz-range-thumb {
    background: var(--color-control-border-strong);
    box-shadow: none;
}

.slider::-webkit-slider-thumb:hover {
    transform: scale(1.2);
}

.slider::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--color-control-accent);
    cursor: pointer;
    border: 3px solid var(--color-control-bg);
    box-shadow: var(--shadow-xs);
}

.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
    transition:
        opacity var(--motion-duration-moderate) var(--motion-ease-standard),
        transform var(--motion-duration-moderate) var(--motion-ease-standard);
}

.slide-left-enter-from {
    opacity: 0;
    transform: translateX(40px);
}
.slide-left-leave-to {
    opacity: 0;
    transform: translateX(-40px);
}
.slide-right-enter-from {
    opacity: 0;
    transform: translateX(-40px);
}
.slide-right-leave-to {
    opacity: 0;
    transform: translateX(40px);
}

@media (prefers-reduced-motion: reduce) {
    .slide-left-enter-active,
    .slide-left-leave-active,
    .slide-right-enter-active,
    .slide-right-leave-active {
        transition-property: opacity;
    }

    .slide-left-enter-from,
    .slide-left-leave-to,
    .slide-right-enter-from,
    .slide-right-leave-to {
        transform: none;
    }
}
</style>
